import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Spinner } from '@ui';
import LoginPage from './pages/auth/LoginPage';
import DesignSystemPage from './pages/DesignSystemPage';
import DashboardPage from './pages/DashboardPage';
import ModulePlaceholder from './pages/ModulePlaceholder';
import AppShell from './layouts/AppShell';
import DevToolbar from './components/DevNavBar';
import { useAppState } from './hooks/useAppState';
import { MODULE_ITEMS } from './data/navigation';
import { canAccessDevToolbar } from './data/settingsSelectors';
import ProtectedRoute from './components/ProtectedRoute';
import { BroadcastQueueProvider } from './contexts/BroadcastQueueContext';
import { CataloguePdfSendProvider } from './contexts/CataloguePdfSendContext';

const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const SupplierPage = lazy(() => import('./pages/SupplierPage'));
const SupplierAddPage = lazy(() => import('./pages/SupplierAddPage'));
const SupplierEditPage = lazy(() => import('./pages/SupplierEditPage'));
const CustomerPage = lazy(() => import('./pages/CustomerPage'));
const CustomerAddPage = lazy(() => import('./pages/CustomerAddPage'));
const CustomerEditPage = lazy(() => import('./pages/CustomerEditPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const ProductAddPage = lazy(() => import('./pages/ProductAddPage'));
const ProductEditPage = lazy(() => import('./pages/ProductEditPage'));
const ProductTypesPage = lazy(() => import('./pages/ProductTypesPage'));
const ProductTypeAddPage = lazy(() => import('./pages/ProductTypeAddPage'));
const ProductCategoriesPage = lazy(() => import('./pages/ProductCategoriesPage'));
const ProductUnitsPage = lazy(() => import('./pages/ProductUnitsPage'));
const ExpenseCategoriesPage = lazy(() => import('./pages/ExpenseCategoriesPage'));
const PurchasePage = lazy(() => import('./pages/PurchasePage'));
const PurchaseAddPage = lazy(() => import('./pages/PurchaseAddPage'));
const PurchaseInvoicePage = lazy(() => import('./pages/PurchaseInvoicePage'));
const SalesPage = lazy(() => import('./pages/SalesPage'));
const SalesAddPage = lazy(() => import('./pages/SalesAddPage'));
const SalesInvoicePage = lazy(() => import('./pages/SalesInvoicePage'));
const ExpensePage = lazy(() => import('./pages/ExpensePage'));
const ExpenseAddPage = lazy(() => import('./pages/ExpenseAddPage'));
const DebtPage = lazy(() => import('./pages/DebtPage'));
const CreditPage = lazy(() => import('./pages/CreditPage'));
const CatalogueListPage = lazy(() => import('./pages/CatalogueListPage'));
const CatalogueAddPage = lazy(() => import('./pages/CatalogueAddPage'));
const CatalogueNewListPage = lazy(() => import('./pages/CatalogueNewListPage'));
const CatalogueNewAddBatchPage = lazy(() => import('./pages/CatalogueNewAddBatchPage'));
const CatalogueProductsPage = lazy(() => import('./pages/CatalogueProductsPage'));
const CatalogueNewCurationPage = lazy(() => import('./pages/CatalogueNewCurationPage'));
const CatalogueNewItemListPage = lazy(() => import('./pages/CatalogueNewItemListPage'));
const CatalogueNewProductListPage = lazy(() => import('./pages/CatalogueNewProductListPage'));
const CatalogueNewPdfListPage = lazy(() => import('./pages/CatalogueNewPdfListPage'));
const CatalogueNewPdfAddPage = lazy(() => import('./pages/CatalogueNewPdfAddPage'));
const CatalogueNewPdfDetailPage = lazy(() => import('./pages/CatalogueNewPdfDetailPage'));
const CatalogueNewPdfViewPage = lazy(() => import('./pages/CatalogueNewPdfViewPage'));
const CatalogueItemListPage = lazy(() => import('./pages/CatalogueItemListPage'));
const CatalogueViewPage = lazy(() => import('./pages/CatalogueViewPage'));
const PromotionListPage = lazy(() => import('./pages/PromotionListPage'));
const PromotionFormPage = lazy(() => import('./pages/PromotionFormPage'));
const PromotionItemListPage = lazy(() => import('./pages/PromotionItemListPage'));
const PromotionViewPage = lazy(() => import('./pages/PromotionViewPage'));
const PromotionLayoutPage = lazy(() => import('./pages/PromotionLayoutPage'));
const PrintCataloguePage = lazy(() => import('./pages/PrintCataloguePage'));
const PrintPromotionPage = lazy(() => import('./pages/PrintPromotionPage'));
const HighlightListPage = lazy(() => import('./pages/HighlightListPage'));
const HighlightAddPage = lazy(() => import('./pages/HighlightAddPage'));
const HighlightItemListPage = lazy(() => import('./pages/HighlightItemListPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const SettingsGeneralPage = lazy(() => import('./pages/SettingsGeneralPage'));
const SettingsUsersPage = lazy(() => import('./pages/SettingsUsersPage'));
const SettingsWhatsAppPage = lazy(() => import('./pages/SettingsWhatsAppPage'));
const SettingsMarketingPage = lazy(() => import('./pages/SettingsMarketingPage'));
const SettingsAiPage = lazy(() => import('./pages/SettingsAiPage'));
const SettingsWhatsAppGatewayPage = lazy(() => import('./pages/SettingsWhatsAppGatewayPage'));
const AboutPage = lazy(() => import('./pages/info/AboutPage'));
const PrivacyPage = lazy(() => import('./pages/info/PrivacyPage'));
const LicensePage = lazy(() => import('./pages/info/LicensePage'));
const HelpPage = lazy(() => import('./pages/info/HelpPage'));
const BroadcastContactsPage = lazy(() => import('./pages/broadcast/BroadcastContactsPage'));
const BroadcastGroupsPage = lazy(() => import('./pages/broadcast/BroadcastGroupsPage'));
const BroadcastHistoryPage = lazy(() => import('./pages/broadcast/BroadcastHistoryPage'));
const BroadcastMonitorPage = lazy(() => import('./pages/broadcast/BroadcastMonitorPage'));
const WhatsAppMessageHistoryPage = lazy(() => import('./pages/broadcast/WhatsAppMessageHistoryPage'));
const BroadcastReportsPage = lazy(() => import('./pages/broadcast/BroadcastReportsPage'));

const EXCLUDED_PATHS = [
  '/dashboard',
  '/reports',
  '/suppliers',
  '/customers',
  '/products',
  '/product-types',
  '/product-categories',
  '/product-units',
  '/purchases',
  '/sales',
  '/expenses',
  '/expense-categories',
  '/debts',
  '/credits',
  '/catalogue',
  '/catalogue/products',
  '/catalogue-new',
  '/catalogue-new/curation',
  '/catalogue-new/list-produk',
  '/catalogue-new/pdf',
  '/promotions',
  '/highlights',
  '/analytics',
  '/settings/general',
  '/settings/users',
  '/settings/whatsapp',
  '/settings/marketing',
  '/settings/ai',
  '/settings/whatsapp-gateway',
  '/broadcast',
  '/broadcast/contacts',
  '/broadcast/groups',
  '/broadcast/activity',
  '/broadcast/campaigns',
  '/broadcast/monitor',
  '/broadcast/messages',
  '/broadcast/reports',
];


function AppRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    theme,
    toggleTheme,
    devToolbarOpen,
    toggleDevToolbar,
    currentUserUsername,
    setCurrentUserUsername,
  } = useAppState();

  const canDevToolbar =
    Boolean(currentUserUsername) && canAccessDevToolbar(currentUserUsername);

  const moduleRoutes = MODULE_ITEMS.filter(
    (item) => !EXCLUDED_PATHS.includes(item.path),
  );

  const isAuthenticated = Boolean(
    localStorage.getItem('auth_token') || localStorage.getItem('auth_user')
  );

  return (
    <>
      <Suspense fallback={<div className="route-loader"><Spinner size={32} label="Memuat halaman…" /></div>}>
        <Routes>
          {/* ── Public / standalone pages ─────────────────────────────── */}
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LoginPage
                  onLoginSuccess={(user) => {
                    const username = typeof user === 'string' ? user : user?.username || 'admin';
                    setCurrentUserUsername(username);
                    navigate('/dashboard', { replace: true });
                  }}
                />
              )
            }
          />
          <Route path="/showcase" element={<DesignSystemPage />} />
          <Route path="/print/catalogue/:code" element={<PrintCataloguePage />} />
          <Route path="/print/promotion/:slug" element={<PrintPromotionPage />} />

          {/* ── Authenticated AppShell ────────────────────────────────── */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppShell />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/suppliers" element={<SupplierPage />} />
              <Route path="/suppliers/add" element={<SupplierAddPage />} />
              <Route path="/suppliers/:id/edit" element={<SupplierEditPage />} />
              <Route path="/customers" element={<CustomerPage />} />
              <Route path="/customers/add" element={<CustomerAddPage />} />
              <Route path="/customers/:id/edit" element={<CustomerEditPage />} />
              <Route path="/products" element={<ProductPage />} />
              <Route path="/products/add" element={<ProductAddPage />} />
              <Route path="/products/:id/edit" element={<ProductEditPage />} />
              <Route path="/product-types" element={<ProductTypesPage />} />
              <Route path="/product-types/add" element={<ProductTypeAddPage />} />
              <Route path="/product-categories" element={<ProductCategoriesPage />} />
              <Route path="/product-units" element={<ProductUnitsPage />} />
              <Route path="/purchases" element={<PurchasePage />} />
              <Route path="/purchases/add" element={<PurchaseAddPage />} />
              <Route path="/purchases/invoice/:trxno" element={<PurchaseInvoicePage />} />
              <Route path="/sales" element={<SalesPage />} />
              <Route path="/sales/add" element={<SalesAddPage />} />
              <Route path="/sales/invoice/:trxno" element={<SalesInvoicePage />} />
              <Route path="/expenses" element={<ExpensePage />} />
              <Route path="/expenses/add" element={<ExpenseAddPage />} />
              <Route path="/expense-categories" element={<ExpenseCategoriesPage />} />
              <Route path="/debts" element={<DebtPage />} />
              <Route path="/credits" element={<CreditPage />} />
              <Route path="/catalogue" element={<CatalogueListPage />} />
              <Route path="/catalogue/add" element={<CatalogueAddPage />} />
              <Route path="/catalogue/products" element={<CatalogueProductsPage />} />
              <Route path="/catalogue/:code" element={<CatalogueItemListPage />} />
              <Route path="/catalogue/:code/view" element={<CatalogueViewPage />} />
              <Route path="/catalogue-new" element={<CatalogueNewListPage />} />
              <Route path="/catalogue-new/add" element={<CatalogueNewAddBatchPage />} />
              <Route path="/catalogue-new/curation" element={<CatalogueNewCurationPage />} />
              <Route path="/catalogue-new/list-produk" element={<CatalogueNewProductListPage />} />
              <Route path="/catalogue-new/pdf" element={<CatalogueNewPdfListPage />} />
              <Route path="/catalogue-new/pdf/add" element={<CatalogueNewPdfAddPage />} />
              <Route path="/catalogue-new/pdf/:code" element={<CatalogueNewPdfDetailPage />} />
              <Route path="/catalogue-new/pdf/:code/view" element={<CatalogueNewPdfViewPage />} />
              <Route path="/catalogue-new/:code" element={<CatalogueNewItemListPage />} />
              <Route path="/promotions" element={<PromotionListPage />} />
              <Route path="/promotions/add" element={<PromotionFormPage key="add" />} />
              <Route path="/promotions/:slug/edit" element={<PromotionFormPage key="edit" />} />
              <Route path="/promotions/:slug" element={<PromotionItemListPage />} />
              <Route path="/promotions/:slug/view" element={<PromotionViewPage />} />
              <Route path="/promotions/:slug/layout" element={<PromotionLayoutPage />} />
              <Route path="/highlights" element={<HighlightListPage />} />
              <Route path="/highlights/add" element={<HighlightAddPage />} />
              <Route path="/highlights/:slug" element={<HighlightItemListPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route
                path="/broadcast"
                element={<Navigate to="/broadcast/contacts" replace />}
              />
              <Route path="/broadcast/contacts" element={<BroadcastContactsPage />} />
              <Route path="/broadcast/groups" element={<BroadcastGroupsPage />} />
              <Route
                path="/broadcast/activity"
                element={<Navigate to="/broadcast/campaigns" replace />}
              />
              <Route path="/broadcast/campaigns" element={<BroadcastHistoryPage />} />
              <Route path="/broadcast/monitor" element={<BroadcastMonitorPage />} />
              <Route path="/broadcast/messages" element={<WhatsAppMessageHistoryPage />} />
              <Route path="/broadcast/reports" element={<BroadcastReportsPage />} />
              <Route
                path="/settings"
                element={<Navigate to="/settings/general" replace />}
              />
              <Route path="/settings/general" element={<SettingsGeneralPage />} />
              <Route path="/settings/users" element={<SettingsUsersPage />} />
              <Route path="/settings/whatsapp" element={<SettingsWhatsAppPage />} />
              <Route path="/settings/marketing" element={<SettingsMarketingPage />} />
              <Route path="/settings/ai" element={<SettingsAiPage />} />
              <Route path="/settings/whatsapp-gateway" element={<SettingsWhatsAppGatewayPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/license" element={<LicensePage />} />
              <Route path="/help" element={<HelpPage />} />
              {moduleRoutes.map((item) => (
                <Route
                  key={item.id}
                  path={item.path}
                  element={<ModulePlaceholder />}
                />
              ))}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>

      {/* Developer-Only Debug Toolbar — rendered on every page.
          Visible only in development (import.meta.env.DEV) and only when
          the logged-in user has a system-level role (super/superadmin/
          debugger) or is the developer/debugger account. */}
      {import.meta.env.DEV && canDevToolbar && (
        <DevToolbar
          currentPath={location.pathname}
          navigateTo={(path) => navigate(path)}
          isOpen={devToolbarOpen}
          onToggle={toggleDevToolbar}
          theme={theme}
          onToggleTheme={toggleTheme}
          isDeveloper={canDevToolbar}
        />
      )}
    </>
  );
}

/**
 * App Root — react-router browser history wrapping all views.
 * The DevToolbar lives here so it persists across all routes.
 */
export default function App() {
  return (
    <BrowserRouter>
      <BroadcastQueueProvider>
        <CataloguePdfSendProvider>
          <AppRoutes />
        </CataloguePdfSendProvider>
      </BroadcastQueueProvider>
    </BrowserRouter>
  );
}
